import React, {useMemo, useState} from 'react';

import {ResponsiveBar, BarDatum} from '@nivo/bar';
import {useStore} from 'effector-react';
import {useTranslation} from 'react-i18next';
import styled from 'styled-components';

import {Spinner} from 'components/ui-kit/Spinner';
import {dashboardState$, dateType$} from 'models/dashboard';
import {colors} from 'theme';
import {DashboardDateType, ProductDefectRecord} from 'types';
import {formatDate} from 'utils/date';
import {formatNumber} from 'utils/number';

import {BlockTitle, BlockTitleDescription, SpinnerLayer} from '../styled';

import {Root, TooltipContainer} from './styled';

const CustomRoot = styled(Root)`
    position: relative;
    display: flex;
    flex-direction: column;
`;

const ChartContainer = styled.div`
    height: 300px;
`;

const CustomTooltip = styled(TooltipContainer)`
    position: relative;
    z-index: 2;
`;

type Props = {
    data: ProductDefectRecord[];
    className?: string;
};

export const ProductDefectChart = ({data, className}: Props) => {
    const {t} = useTranslation();
    const {productDefectPending} = useStore(dashboardState$);
    const dateType = useStore(dateType$);

    const dateFormatByType = dateType === DashboardDateType.Day ? 'HH:mm' : 'dd.MM';

    const mappedData: BarDatum[] = useMemo(
        () =>
            data
                .filter((item) => item.value || data.length < 90)
                .map((item) => ({
                    time: formatDate(item.time, dateFormatByType),
                    value: item.value,
                })),
        [data, dateFormatByType],
    );

    const xGridValues = useMemo(() => mappedData.map((item) => item.time as string), [mappedData]);

    const [tooltip, setTooltip] = useState<{
        value: number;
        x: number;
        y: number;
        visible: boolean;
    } | null>(null);

    const CustomBarItem = ({
        bar,
        borderRadius = 2,
        borderWidth = 1,
        labelColor = '#000',
        onHover,
    }: any) => {
        const {x, y, width, height, borderColor, label, shouldRenderLabel, data} = bar;

        const handleMouseEnter = (e: React.MouseEvent<SVGElement>) => {
            if (onHover) {
                onHover({
                    visible: true,
                    value: data.value,
                    x: e.clientX,
                    y: e.clientY,
                });
            }
        };

        const handleMouseLeave = () => {
            if (onHover) onHover({visible: false});
        };

        return (
            <g
                transform={`translate(${x}, ${y})`}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <rect
                    transform={`translate(0, ${-8.5})`}
                    width={width}
                    height={5.6}
                    fill={colors.brandPrimary}
                    strokeWidth={borderWidth}
                    stroke={borderColor}
                />
                <rect
                    width={width}
                    height={height}
                    rx={borderRadius}
                    ry={borderRadius}
                    fill={colors.brandPrimary + '80'}
                    strokeWidth={borderWidth}
                    stroke={borderColor}
                />
                {shouldRenderLabel && (
                    <text
                        x={width / 2}
                        y={height / 2}
                        textAnchor="middle"
                        dominantBaseline="central"
                        style={{pointerEvents: 'none', fill: labelColor}}
                    >
                        {label}
                    </text>
                )}
            </g>
        );
    };

    return (
        <CustomRoot className={className}>
            <BlockTitle>{t('pages.dashboard.defectChart')}</BlockTitle>
            {productDefectPending ? (
                <SpinnerLayer>
                    <Spinner />
                </SpinnerLayer>
            ) : (
                <>
                    <BlockTitleDescription>
                        {t('pages.dashboard.defectChartDesc')}
                    </BlockTitleDescription>
                    <ChartContainer>
                        <ResponsiveBar
                            data={mappedData}
                            keys={['value']}
                            indexBy="time"
                            margin={{top: 20, right: 0, bottom: 75, left: 0}}
                            padding={0.3}
                            valueScale={{type: 'linear'}}
                            indexScale={{type: 'band', round: true}}
                            enableGridY={false}
                            colors={colors.brandPrimary}
                            borderColor={{from: 'color', modifiers: [['darker', 1.6]]}}
                            axisTop={null}
                            axisLeft={null}
                            axisRight={null}
                            axisBottom={{
                                tickSize: 0,
                                tickPadding: 35,
                                tickValues: xGridValues,
                                tickRotation: 270,
                            }}
                            theme={{
                                axis: {
                                    ticks: {
                                        text: {
                                            fill: colors.textSecondary,
                                            fontSize: 12,
                                        },
                                    },
                                },
                            }}
                            enableLabel={false}
                            barComponent={(props) => (
                                <CustomBarItem
                                    {...props}
                                    onHover={(tooltipData: any) => setTooltip(tooltipData)}
                                />
                            )}
                        />

                        {tooltip?.visible && (
                            <CustomTooltip
                                style={{
                                    position: 'fixed',
                                    top: tooltip.y + 10,
                                    left: tooltip.x + 10,
                                    pointerEvents: 'none',
                                }}
                            >
                                {formatNumber(tooltip.value)}
                            </CustomTooltip>
                        )}
                    </ChartContainer>
                </>
            )}
        </CustomRoot>
    );
};
